<script setup>
import { ref, computed, watch } from 'vue'
import { Head, router, useForm } from '@inertiajs/vue3'
import {
    PhCalendar, PhMapPin, PhCheckCircle, PhCalendarPlus,
} from '@phosphor-icons/vue'
import Layout   from '@pages/Shared/layouts/AdminLayout.vue'
import Table    from '@pages/Shared/ui/Table.vue'
import Modal    from '@pages/Shared/Modal.vue'
import Select   from '@pages/Shared/form/Select.vue'
import Input    from '@pages/Shared/form/Input.vue'
import Textarea from '@pages/Shared/form/Textarea.vue'
import Spinner  from '@pages/Shared/ui/Spinner.vue'
import { flashToast } from '@/toast.js'

const props = defineProps({
    interviews:        Object,
    sessions:          Array,
    sections:          Array,
    activeSession:     Object,
    counts:            Object,
    interviewStatuses: Array,
    filters:           Object,
})

// ── Filters ────────────────────────────────────────────────────────────────
const filters = ref({
    search: null, session_id: null, section_id: null, status: null,
    ...props.filters,
})

watch(() => props.filters, val => { filters.value = { ...val } }, { deep: true })

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined && v !== '')
    )
    router.get('/admin/interviews', cleaned, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch = v => { filters.value.search = v; applyFilters() }
const handleFilter = f => { filters.value = { ...filters.value, ...f }; applyFilters() }

// ── Batch Schedule Modal ───────────────────────────────────────────────────
// Schedules ALL submitted applications at once — no per-row target
const scheduleModal = ref(false)
const scheduleForm  = useForm({ scheduled_at: '', venue: '', invitation_note: '' })

const submitSchedule = () => {
    scheduleForm.post('/admin/interviews/schedule', {
        preserveScroll: true,
        onSuccess: page => {
            scheduleModal.value = false
            scheduleForm.reset()
            flashToast(page.props.flash)
        },
        onError: errors => console.log('Schedule errors:', errors),
    })
}

// ── Batch Reschedule Modal ─────────────────────────────────────────────────
// Reschedules ALL scheduled/absent interviews — no per-row target
const showRescheduleModal = ref(false)
const rescheduleForm      = useForm({ scheduled_at: '', venue: '', invitation_note: '' })

const submitReschedule = () => {
    rescheduleForm.post('/admin/interviews/reschedule', {
        preserveScroll: true,
        onSuccess: page => {
            showRescheduleModal.value = false
            rescheduleForm.reset()
            flashToast(page.props.flash)
        },
        onError: errors => console.log('Reschedule errors:', errors),
    })
}

// ── Record Outcome Modal (per-row — stays individual) ──────────────────────
const activeInterview = ref(null)
const showRecordModal = ref(false)
const recordForm      = useForm({ status: 'completed', score: null, remarks: '', conducted_at: '' })

const openRecord = row => {
    activeInterview.value   = row
    recordForm.status       = 'completed'
    recordForm.score        = row.score ?? null
    recordForm.remarks      = row.remarks ?? ''
    recordForm.conducted_at = ''
    showRecordModal.value   = true
}

const submitRecord = () => {
    recordForm.post(
        `/admin/interviews/${activeInterview.value.application_id}/record`,
        {
            preserveScroll: true,
            preserveState:  true,
            onSuccess: page => {
                showRecordModal.value = false
                recordForm.reset()
                flashToast(page.props.flash)
            },
            onError: errors => console.log('Record errors:', errors),
        }
    )
}

// ── Options ────────────────────────────────────────────────────────────────
const sectionOptions = computed(() =>
    (props.sections ?? []).map(s => ({ label: s.name, value: s.id }))
)

const columns = [
    { key: 'index',     label: '#',            type: 'index' },
    { key: 'applicant', label: 'Applicant',    type: 'slot'  },
    { key: 'section',   label: 'Section',      type: 'slot'  },
    { key: 'scheduled', label: 'Scheduled',    type: 'slot'  },
    { key: 'venue',     label: 'Venue',        type: 'slot'  },
    { key: 'score',     label: 'Score',        type: 'slot'  },
    { key: 'conductor', label: 'Conducted by', type: 'slot'  },
    { key: 'status',    label: 'Status',       type: 'slot'  },
    { key: 'actions',   label: '',             type: 'slot'  },
]
</script>

<template>
    <Layout>
        <Head title="Interviews" />

        <!-- ── Batch Schedule Modal ──────────────────────────────────────── -->
        <Modal v-model="scheduleModal" title="Schedule Interviews" @close="scheduleModal = false">
            <form class="p-4 md:p-5" @submit.prevent="submitSchedule">
                <p class="text-sm text-gray-500 mb-4">
                    All <strong>submitted</strong> applications in the active session will be
                    invited for interview at this date and venue.
                </p>
                <Input label="Date &amp; Time" v-model="scheduleForm.scheduled_at"
                    type="datetime-local" :error="scheduleForm.errors.scheduled_at" class="mb-4" />
                <Input label="Venue (optional)" v-model="scheduleForm.venue"
                    placeholder="e.g. Admin Office, Room 1" class="mb-4" />
                <Textarea label="Invitation Note (optional)" v-model="scheduleForm.invitation_note"
                    :rows="3" class="mb-4" />
                <button type="submit" :disabled="scheduleForm.processing"
                    class="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white
                           text-sm font-semibold flex justify-center items-center gap-2 transition">
                    <Spinner v-if="scheduleForm.processing" :size="14" />
                    Schedule &amp; Invite All
                </button>
            </form>
        </Modal>

        <!-- ── Batch Reschedule Modal ────────────────────────────────────── -->
        <Modal v-model="showRescheduleModal" title="Reschedule Outstanding Interviews"
            @close="showRescheduleModal = false">
            <form class="p-4 md:p-5" @submit.prevent="submitReschedule">
                <p class="text-sm text-gray-500 mb-4">
                    All <strong>scheduled</strong> and <strong>absent</strong> interviews will
                    be moved to the new date. This covers applicants from the first batch who
                    haven't been recorded yet, and those who didn't attend.
                </p>
                <Input label="New Date &amp; Time" v-model="rescheduleForm.scheduled_at"
                    type="datetime-local" :error="rescheduleForm.errors.scheduled_at" class="mb-4" />
                <Input label="Venue (optional)" v-model="rescheduleForm.venue"
                    placeholder="e.g. Admin Office, Room 1" class="mb-4" />
                <Textarea label="Note (optional)" v-model="rescheduleForm.invitation_note"
                    :rows="3" class="mb-4" />
                <button type="submit" :disabled="rescheduleForm.processing"
                    class="w-full py-3 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white
                           text-sm font-semibold flex justify-center items-center gap-2 transition">
                    <Spinner v-if="rescheduleForm.processing" :size="14" />
                    Reschedule Outstanding
                </button>
            </form>
        </Modal>

        <!-- ── Record Outcome Modal (per-row) ───────────────────────────── -->
        <Modal v-model="showRecordModal" title="Record Interview Outcome"
            @close="showRecordModal = false">
            <form class="p-4 md:p-5" @submit.prevent="submitRecord">
                <p class="text-sm text-gray-500 mb-4">
                    Recording outcome for
                    <strong>
                        {{ activeInterview?.application?.applicant?.first_name }}
                        {{ activeInterview?.application?.applicant?.last_name }}
                    </strong>
                </p>
                <Select label="Outcome" v-model="recordForm.status"
                    :options="[
                        { label: 'Completed', value: 'completed' },
                        { label: 'Absent',    value: 'absent'    },
                    ]"
                    placeholder="Select outcome" :error="recordForm.errors.status" class="mb-4" />
                <Input label="Score (0–100)" v-model="recordForm.score" type="number"
                    min="0" max="100" placeholder="Optional" class="mb-4" />
                <Textarea label="Remarks" v-model="recordForm.remarks" :rows="3" class="mb-4" />
                <Input label="Conducted At" v-model="recordForm.conducted_at"
                    type="datetime-local" class="mb-4" />
                <button type="submit" :disabled="recordForm.processing"
                    class="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white
                           text-sm font-semibold flex justify-center items-center gap-2 transition">
                    <Spinner v-if="recordForm.processing" :size="14" />
                    Save Outcome
                </button>
            </form>
        </Modal>

        <!-- ── Header ───────────────────────────────────────────────────── -->
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Interviews</h2>
                <p class="text-sm text-gray-500 mt-0.5">Interview schedule and outcomes</p>
            </div>
            <div class="flex flex-wrap items-center gap-2">

                <!-- Schedule all submitted -->
                <button
                    @click="scheduleModal = true"
                    class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                           text-white bg-primary hover:bg-primary-light rounded-lg transition"
                >
                    <PhCalendar :size="16" />
                    Schedule Batch
                </button>
                <!-- Reschedule outstanding -->
                <button
                    @click="showRescheduleModal = true"
                    class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                           text-white bg-yellow-500 hover:bg-yellow-600 rounded-lg transition"
                >
                    <PhCalendarPlus :size="16" />
                    Reschedule Outstanding
                </button>
            </div>
        </div>

        <!-- ── Status Tabs ───────────────────────────────────────────────── -->
        <div class="flex items-center gap-2 mb-5 flex-wrap">
            <button
                v-for="s in [{ value: null, label: 'All' }, ...interviewStatuses.map(s => ({ value: s.value, label: s.value }))]"
                :key="s.value"
                @click="filters.status = s.value; applyFilters()"
                class="px-3 py-1.5 rounded-lg text-xs font-semibold border transition capitalize"
                :class="filters.status === s.value
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-primary'"
            >
                {{ s.label }}
                <span v-if="s.value && counts?.[s.value]"
                    class="ml-1 bg-white/20 px-1.5 py-0.5 rounded-full text-[10px]">
                    {{ counts[s.value] }}
                </span>
            </button>
        </div>

        <!-- ── Table ─────────────────────────────────────────────────────── -->
        <Table
            :data="interviews"
            :columns="columns"
            row-key="id"
            :exportable="true"
            :actions="{ view: true }"
            :filters="[
                {
                    key: 'session_id', label: 'Session',
                    options: [
                        { label: 'All Sessions', value: null },
                        ...(sessions ?? []).map(s => ({ label: s.name, value: s.id })),
                    ],
                    value: filters.session_id,
                },
                {
                    key: 'section_id', label: 'Section',
                    options: [{ label: 'All Sections', value: null }, ...sectionOptions],
                    value: filters.section_id,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @view="id => router.get(`/admin/applications/${interviews.data?.find(i => i.id === id)?.application_id}`)"
        >
            <template #applicant="{ row }">
                <div>
                    <p class="text-sm font-medium text-gray-800">
                        {{ row.application?.applicant?.first_name }}
                        {{ row.application?.applicant?.last_name }}
                    </p>
                    <p class="text-xs text-gray-400">{{ row.application?.applicant?.id }}</p>
                </div>
            </template>

            <template #section="{ row }">
                <span class="text-sm text-gray-600">{{ row.application?.section?.name ?? '—' }}</span>
            </template>

            <template #scheduled="{ row }">
                <div class="flex items-center gap-1.5 text-sm text-gray-600">
                    <PhCalendar :size="14" class="text-gray-400" />
                    {{ row.scheduled_at ? new Date(row.scheduled_at).toLocaleString() : '—' }}
                </div>
            </template>

            <template #venue="{ row }">
                <div class="flex items-center gap-1.5 text-sm text-gray-500">
                    <PhMapPin :size="14" class="text-gray-400" />
                    {{ row.venue ?? '—' }}
                </div>
            </template>

            <template #score="{ row }">
                <span class="text-sm text-gray-700 font-medium">
                    {{ row.score !== null ? `${row.score}/100` : '—' }}
                </span>
            </template>

            <template #conductor="{ row }">
                <span class="text-sm text-gray-600">{{ row.conductor?.name ?? '—' }}</span>
            </template>

            <template #status="{ row }">
                <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold capitalize"
                    :class="{
                        'bg-primary-50 text-primary-700':     row.status === 'scheduled',
                        'bg-green-50 text-green-700':   row.status === 'completed',
                        'bg-red-50 text-red-600':       row.status === 'absent',
                        'bg-yellow-50 text-yellow-700': row.status === 'rescheduled',
                        'bg-gray-100 text-gray-500':    row.status === 'skipped',
                    }"
                >
                    <span class="w-1.5 h-1.5 rounded-full"
                        :class="{
                            'bg-primary-500':   row.status === 'scheduled',
                            'bg-green-500':  row.status === 'completed',
                            'bg-red-500':    row.status === 'absent',
                            'bg-yellow-500': row.status === 'rescheduled',
                            'bg-gray-400':   row.status === 'skipped',
                        }">
                    </span>
                    {{ row.status }}
                </span>
            </template>

            <template #actions="{ row }">
                <div class="flex items-center gap-2">
                    <!-- Record: per-row, matches Table built-in action pill style -->
                    <button
                        v-if="['scheduled', 'rescheduled'].includes(row.status)"
                        @click="openRecord(row)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-green-600 bg-green-50 hover:bg-green-100 rounded-md border border-green-100 transition"
                    >
                        <PhCheckCircle :size="12" />
                        Record
                    </button>
                </div>
            </template>
        </Table>
    </Layout>
</template>